import React from "react";
import type { PolicyPage } from "../lib/router";

const SUPPORT_EMAIL = "hello@checkthelineup.com";
const EFFECTIVE_DATE = "May 2026";

export function Policy({ page, onBack }: { page: PolicyPage; onBack: () => void }) {
  const content = PAGES[page];
  return (
    <div data-testid={`view-${page}`}>
      <div className="topbar">
        <div className="brand">
          <button
            onClick={onBack}
            aria-label="Back"
            data-testid="back-to-events"
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "4px 10px 4px 6px",
              border: "1px solid var(--panel-edge)",
              background: "var(--bg-2)",
              borderRadius: 999,
              color: "var(--ink)",
              fontSize: 12, letterSpacing: "0.08em",
              textTransform: "uppercase", fontWeight: 600,
            }}
          >
            <span style={{ fontSize: 14 }}>‹</span> Home
          </button>
        </div>
      </div>

      <article className="policy">
        <div className="section-head">
          <div>
            <span className="super">LEGAL</span>
            <h2>{content.title}</h2>
            <div className="policy-effective">Effective {EFFECTIVE_DATE}</div>
          </div>
        </div>
        <div className="policy-body">{content.body}</div>
        <nav className="policy-nav" aria-label="Legal pages">
          {(Object.entries(PAGES) as [PolicyPage, typeof PAGES[PolicyPage]][])
            .filter(([k]) => k !== page)
            .map(([key, p]) => (
              <a key={key} href={`/${key}`}>{p.title}</a>
            ))}
        </nav>
      </article>
    </div>
  );
}

type PageContent = { title: string; body: React.ReactNode };

const PAGES: Record<PolicyPage, PageContent> = {
  privacy: {
    title: "Privacy Policy",
    body: (
      <>
        <p>
          The short version: <strong>we do not collect, sell, or share
          personal data.</strong> You can use CheckTheLineUp without an
          account and without revealing who you are.
        </p>

        <h3>What we store and where</h3>
        <p>
          Your preferences — the artists you pass, meh, like, or superlike —
          are saved on your device only. They don't leave your device, and
          we can't see them. Clearing your browser storage or uninstalling
          the app wipes them permanently.
        </p>

        <h3>What we don't collect</h3>
        <ul>
          <li>No account. No sign-up, email, or password required.</li>
          <li>No personal information (name, address, phone).</li>
          <li>No precise location.</li>
          <li>No advertising identifiers, no cross-site tracking.</li>
        </ul>

        <h3>Standard server logs</h3>
        <p>
          When your app requests festival data from our server, our hosting
          provider briefly logs the request (IP address, user-agent, path)
          for operational and security reasons. We don't use those logs
          for analytics or advertising.
        </p>

        <h3>Embedded players</h3>
        <p>
          When you play an audio or video widget inside an artist page, that
          playback happens through the provider (for example YouTube or
          SoundCloud). The provider receives the standard web-request
          information you'd send by visiting their site directly, and
          applies its own privacy policy to your interaction with the
          player.
        </p>

        <h3>Analytics</h3>
        <p>
          The web app uses <strong>Google Analytics 4</strong> to measure
          aggregate usage so we can decide which festivals to add and
          whether features work. We've enabled the privacy-protective
          defaults:
        </p>
        <ul>
          <li>IP anonymisation is on.</li>
          <li>Google Signals (cross-device profiling) is disabled.</li>
          <li>Advertising personalisation is disabled.</li>
          <li>We never send your swipe picks, schedule, or any on-device preferences to analytics. Only the page you're viewing and standard engagement metrics.</li>
        </ul>
        <p>
          Any tracker-blocker will block analytics entirely, with no impact
          on functionality.
        </p>

        <h3>Cookies</h3>
        <p>
          The app itself sets no cookies. Google Analytics sets first-party
          cookies used only to count unique visits; you can clear them via
          your browser's site-data controls at any time.
        </p>

        <h3>Children</h3>
        <p>
          The app doesn't knowingly collect information from children under
          13 (or the equivalent minimum age in your jurisdiction). Because
          we collect no personal data from anyone, we treat every user the
          same.
        </p>

        <h3>Affiliate links</h3>
        <p>
          Some outbound links (for example the Tickets chip on a festival
          card) may be affiliate links. No personal information is sent in
          those clicks. See the <a href="/disclosure">Disclosure</a> page.
        </p>

        <h3>Changes to this policy</h3>
        <p>
          Material changes will be announced in-app before taking effect.
          The effective date at the top of this page shows the current
          version.
        </p>

        <h3>Contact</h3>
        <p>
          Questions? Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </p>
      </>
    ),
  },

  terms: {
    title: "Terms of Service",
    body: (
      <>
        <p>
          By using CheckTheLineUp (the "App") you agree to these terms.
          Please read them carefully.
        </p>

        <h3>The App</h3>
        <p>
          The App is a free tool for exploring music-festival lineups,
          building a personal schedule, and identifying set-time conflicts.
          It is provided for personal, non-commercial use.
        </p>

        <h3>Festival and artist data</h3>
        <p>
          Lineup information is assembled from publicly announced sources.
          Set times published before the festival's official schedule is
          released are illustrative and may not reflect final programming.
          We make no guarantees of accuracy, completeness, or availability.
        </p>

        <h3>Third-party content</h3>
        <p>
          Artist names, images, audio, and other metadata belong to their
          respective owners. The app displays this content for
          informational purposes. Embedded audio and video is subject to
          the terms of the platform that hosts it.
        </p>

        <h3>No affiliation</h3>
        <p>
          CheckTheLineUp is an independent project and is not affiliated
          with, endorsed by, or sponsored by any festival organiser,
          featured artist, or referenced service. All trademarks are the
          property of their respective owners.
        </p>

        <h3>No warranty</h3>
        <p>
          The App is provided "AS IS", without warranty of any kind,
          express or implied, including the warranties of merchantability,
          fitness for a particular purpose, and non-infringement.
        </p>

        <h3>Limitation of liability</h3>
        <p>
          To the fullest extent permitted by law, the developers of
          CheckTheLineUp shall not be liable for any indirect, incidental,
          special, consequential, or punitive damages arising from your
          use of the App.
        </p>

        <h3>DMCA / takedown requests</h3>
        <p>
          If you are an artist, rights-holder, festival organiser, or
          authorised representative and you believe any content in this
          app infringes your rights or misrepresents you, email{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> with a
          description of what you'd like removed or corrected, a link to
          the page or artist in question, and a statement that you're
          authorised to act.
        </p>
        <p>
          We honour valid requests promptly, typically within a few
          business days. Nothing here limits your other rights under
          copyright or trademark law.
        </p>

        <h3>Changes</h3>
        <p>
          These terms may be updated from time to time. Continued use of
          the App after changes constitutes acceptance of the updated
          terms.
        </p>

        <h3>Contact</h3>
        <p>
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>
      </>
    ),
  },

  disclosure: {
    title: "Affiliate & Commercial Disclosure",
    body: (
      <>
        <p>
          Some of the outbound links in this app — for example the{" "}
          <em>Tickets</em> chip on a festival card — may be{" "}
          <strong>affiliate links</strong>. If you click one and later
          complete a qualifying purchase with the destination merchant,
          CheckTheLineUp may earn a small commission from the merchant.{" "}
          <strong>You pay the same price as going direct.</strong>
        </p>

        <p>
          Affiliate revenue, if any, does not influence the order, rating,
          or prominence of any festival or artist in the app. Lineup data
          comes from official festival announcements and public
          music-metadata sources — not paid placement.
        </p>

        <p>
          This disclosure, together with the in-line notice shown next to
          each affiliate chip, is provided per the US Federal Trade
          Commission{" "}
          <a
            href="https://www.ftc.gov/legal-library/browse/rules/guides-concerning-use-endorsements-testimonials-advertising"
            target="_blank"
            rel="noreferrer"
          >
            16 CFR Part 255
          </a>{" "}
          and equivalent guidelines in other jurisdictions.
        </p>

        <h3>Contact</h3>
        <p>
          Questions or corrections:{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </p>
      </>
    ),
  },

  about: {
    title: "About CheckTheLineUp",
    body: (
      <>
        <p>
          <strong>CheckTheLineUp</strong> is a mobile-first web app (with
          iOS and Android native builds) that turns a festival bill into a
          dating-app-style swipe deck, then builds your personal schedule
          and flags conflicts between overlapping sets you want to catch.
        </p>

        <h3>Who runs this</h3>
        <p>
          CheckTheLineUp is an independent project, designed and operated
          from the United States. It launched in 2026 with EDC Las Vegas
          as the seed event and has since expanded to {""}
          <a href="/events">a catalogue of festivals</a> across the US,
          Europe, Latin America, and Asia. For business or editorial
          enquiries, email{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. Follow
          along on Instagram at{" "}
          <a
            href="https://instagram.com/yocheckthelineup"
            target="_blank"
            rel="noopener noreferrer me"
          >@yocheckthelineup</a>.
        </p>

        <h3>How we make money</h3>
        <p>
          The app is free and ad-free. Some outbound links may be
          affiliate links — see the{" "}
          <a href="/disclosure">Disclosure</a> page for details.
        </p>

        <h3>Install on your device</h3>
        <p>
          On mobile Safari or Chrome, tap the Share or menu button and
          choose "Add to Home Screen" — the app installs as a standalone
          experience with offline support. Native iOS and Android builds
          are also available.
        </p>

        <h3>Credits</h3>
        <p>
          Lineup, date, stage, and venue data comes from official festival
          announcements. Artist photos, embedded players, and genre tags
          are served by their respective music platforms under their own
          terms.
        </p>

        <h3>Trademarks</h3>
        <p>
          All artist names, festival names, stage names, and logos
          referenced in the app are the property of their respective
          owners. CheckTheLineUp is an independent, unaffiliated project.
          Rights holders who'd like content removed or corrected can reach
          us via <a href="/support">Support</a>.
        </p>
      </>
    ),
  },

  support: {
    title: "Support",
    body: (
      <>
        <p>
          Found a bug, have a feature request, or spotted an artist with
          the wrong info? We read every message.
        </p>

        <h3>Contact</h3>
        <p style={{ fontSize: 16 }}>
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>

        <h3>Frequently asked</h3>
        <p><strong>Why is my schedule empty after I reinstall the app?</strong></p>
        <p>
          Preferences are stored locally on each device. Uninstalling the
          app (or clearing browser data) wipes them. We're considering an
          optional cloud-sync feature — tell us at the address above if
          you'd use it.
        </p>
        <p><strong>Why does a YouTube video say "unavailable"?</strong></p>
        <p>
          Some uploaders disable embedding on their videos. Tap the
          "Watch on YouTube" link inside the artist sheet to open the
          video in YouTube directly.
        </p>
        <p><strong>The set times seem wrong.</strong></p>
        <p>
          Festivals typically publish their official set times only days
          before the show. Until then, times shown in the app are
          evenly-spaced illustrations based on who is billed at each
          stage. We update the schedule the moment official times drop.
        </p>
        <p><strong>Can I see another festival?</strong></p>
        <p>
          CheckTheLineUp is designed to support many festivals. More events
          are rolling in — email us the festival you'd like to see first.
        </p>
      </>
    ),
  },
};
